import React, { ComponentPropsWithoutRef, useEffect, useRef, useState } from 'react'

import useTranslation from 'next-translate/useTranslation'

import clsx from 'clsx'
import { Control, useController } from 'react-hook-form'

import { passwordPattern, phoneNumberPattern } from '@/utils/data/trials/form'

export interface InputType extends ComponentPropsWithoutRef<'input'> {
	className?: string
	name: string
	label: string
	control?: Control
	rules?: any
	errored?: boolean
	pattern?: string
	type?: string
	hintText?: string | null
}

export function TrialInput({
	className,
	name,
	label,
	control,
	rules,
	errored,
	pattern,
	type,
	hintText,
	...props
}: InputType) {
	const { t } = useTranslation('trials')
	const {
		field: { onChange, onBlur, name: fieldName, ref },
		fieldState: { error }
	} = useController({
		name,
		control,
		rules,
		defaultValue: props.defaultValue ?? ''
	})

	const inputRef = useRef<HTMLInputElement>(null)

	const [isInputActive, setIsInputActive] = useState(false)
	const [inputValue, setInputValue] = useState('')

	// Set the input value to the default value if it exists
	useEffect(() => {
		if (inputRef.current) {
			ref(inputRef.current)
			if (inputRef.current.value) {
				onChange(inputRef.current.value)
			}
		}
	}, [ref, name, onChange])

	// Set the input value to the default value if it exists
	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault()
		const newInputValue = event.target.value
		setIsInputActive(true)
		setInputValue(newInputValue)
		onChange(event)
	}

	const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault()
		onBlur()
		setIsInputActive(false)
	}

	const handleFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.preventDefault()
		setIsInputActive(true)
	}

	const passwordValidation = (type: string) => {
		switch (type) {
			case 'special':
				return inputValue.match(/\W/)

			case 'length':
				return inputValue?.length >= 12

			case 'number':
				return inputValue.match(/[0-9]/)

			case 'uppercaseAndLowercase':
				return inputValue.match(/^(?=.*?[a-z])(?=.*?[A-Z])/)

			default:
				break
		}
	}

	return (
		<div>
			<div className="flex flex-col relative">
				{label ? (
					<label
						htmlFor={name}
						className={clsx(
							'absolute transition-all text-gray-600',
							isInputActive || inputValue || inputRef?.current?.value
								? '-top-4 text-[11px] text-bold'
								: 'bottom-0 peer-focus-within:-top-6 text-base',
							rules.required ? 'required' : ''
						)}
					>
						{label}
					</label>
				) : null}

				<input
					type={type ?? 'text'}
					id={fieldName}
					className={clsx(
						className,
						'w-full bg-transparent outline-none transition-all',
						isInputActive || inputValue || inputRef?.current?.value ? 'translate-y-0' : ''
					)}
					name={fieldName}
					ref={inputRef}
					onChange={handleInputChange}
					onBlur={handleBlur}
					onFocus={handleFocus}
					pattern={pattern}
					{...props}
				/>

				{/* Bottom black bar */}
				<div className="border-b border-bc-black" />
			</div>

			{/* Hint text */}
			{hintText && fieldName !== 'phone_number' ? (
				<p className="text-xs text-gray-600">{hintText}</p>
			) : null}

			{/* Error message */}
			{!!error?.message && fieldName !== 'password' ? (
				<p className="text-xs text-bc-pink">{error.message}</p>
			) : null}

			{/* Phone number error message */}
			{fieldName === 'phone_number' && ((isInputActive && inputValue) || (error && inputValue)) && (
				<div className="text-xs">
					<p
						className={clsx(
							RegExp(phoneNumberPattern).exec(inputValue) ? 'text-bc-green-100' : 'text-bc-pink'
						)}
					>
						{t('trialForm.fields.phone_number.errors.invalid')}
					</p>
				</div>
			)}

			{/* Password message */}
			{fieldName === 'password' && (error || isInputActive) && (
				<div className="text-xs">
					<p
						className={clsx(
							isInputActive && !inputValue
								? 'text-gray-600'
								: RegExp(passwordPattern).exec(inputValue)
								? 'text-bc-green-100'
								: 'text-bc-pink'
						)}
					>
						{t('trialForm.fields.password.hintText.title')}
					</p>

					<ul
						className={clsx(
							isInputActive && !inputValue ? 'p-[17px] list-disc' : 'pt-4 pl-0.5 pr-[17px]'
						)}
					>
						<li
							className={clsx(
								isInputActive && !inputValue
									? 'text-gray-600'
									: passwordValidation('special')
									? 'text-bc-green-100'
									: 'text-bc-pink'
							)}
						>
							{isInputActive && !inputValue ? '' : passwordValidation('special') ? '✓ ' : 'X '}
							{t('trialForm.fields.password.hintText.special')}
						</li>
						<li
							className={clsx(
								isInputActive && !inputValue
									? 'text-gray-600'
									: passwordValidation('length')
									? 'text-bc-green-100'
									: 'text-bc-pink'
							)}
						>
							{isInputActive && !inputValue ? '' : passwordValidation('length') ? '✓ ' : 'X '}
							{t('trialForm.fields.password.hintText.minLength')}
						</li>
						<li
							className={clsx(
								isInputActive && !inputValue
									? 'text-gray-600'
									: passwordValidation('number')
									? 'text-bc-green-100'
									: 'text-bc-pink'
							)}
						>
							{isInputActive && !inputValue ? '' : passwordValidation('number') ? '✓ ' : 'X '}
							{t('trialForm.fields.password.hintText.number')}
						</li>
						<li
							className={clsx(
								isInputActive && !inputValue
									? 'text-gray-600'
									: passwordValidation('uppercaseAndLowercase')
									? 'text-bc-green-100'
									: 'text-bc-pink'
							)}
						>
							{isInputActive && !inputValue
								? ''
								: passwordValidation('uppercaseAndLowercase')
								? '✓ '
								: 'X '}
							{t('trialForm.fields.password.hintText.uppercaseAndLowercase')}
						</li>
					</ul>
				</div>
			)}
		</div>
	)
}
